/**
 * @file ErrorState.tsx
 * @brief Inline error surface — typically rendered when a TanStack Query
 *        fails. Includes optional retry action.
 *
 * @author DES
 * @date 26-05-2026
 */

import { RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";

interface ErrorStateProps {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}

function describe(err: unknown): string {
  if (err instanceof ApiClientError) {
    return err.message || "Request failed";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export function ErrorState({ title, error, onRetry, className }: ErrorStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center",
        className,
      )}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <TriangleAlert className="h-5 w-5" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-destructive">
          {title ?? "Failed to load"}
        </p>
        <p className="max-w-sm text-xs text-muted-foreground">
          {describe(error)}
        </p>
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}
